import { Component, ViewEncapsulation, ChangeDetectionStrategy, Input } from "@angular/core";

import { ArgTypes } from "@storybook/angular/types-6-0";
import { BooleanInput, coerceBooleanProperty, InputDate } from "@vattenfall/util";

import { DatesElementAccessor } from "../dates-accessor";

export const argTypesDate: ArgTypes = <const>{
  format: {
    control: "text",
    description:
      "Date format string. The characters in the format string wrapped between two single quotes characters (') are escaped. Two single quotes in a row, whether inside or outside a quoted sequence, represent a 'real' single quote. Default is `yyyy-MM-dd`.",
    table: {
      category: "Inputs",
      type: {
        summary: "string",
      },
    },
  },
  weekday: {
    control: "boolean",
    description:
      "An attribute which, if present, disables all non-weekdays in calendar from user interaction. Public holidays will be disabled for Swedish and Finnish locale, otherwise weekends only.",
    defaultValue: false,
    table: {
      category: "Inputs",
      type: {
        summary: "boolean",
      },
    },
  },
};

/**
 * A control for entering a date (year, month, and day, with no time zone).
 *
 * A string representing the date entered in the input. The date is formatted according to ISO8601, described in Date strings format. Values can also be provided as a `Date` object, in which case the properties ending in `AsDate` should be used. A custom `format` can be provided to support regional variations for displaying dates.
 *
 * Text inputs can be used as an Angular `FormControl`, in both reactive and template driven forms.
 */
@Component({
  selector: "ui-input[type=date]",
  templateUrl: "./date.component.html",
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputDateComponent extends DatesElementAccessor<InputDate> {
  /**
   * Date format string.
   *
   * The characters in the format string wrapped between two single quotes characters (') are escaped. Two single quotes in a row, whether inside or outside a quoted sequence, represent a 'real' single quote.
   *
   * Read more at [date-fns](https://date-fns.org/v2.29.3/docs/parse).
   *
   * Default is `yyyy-MM-dd`.
   */
  @Input() format = "yyyy-MM-dd";
  /**
   * An attribute which, if present, disables all non-weekdays in calendar from user interaction.
   *
   * Public holidays will be disabled for Swedish and Finnish locale, otherwise weekends only.
   */
  @Input()
  get weekday(): boolean {
    return this._weekday;
  }
  set weekday(value: BooleanInput) {
    this._weekday = coerceBooleanProperty(value);
  }
  private _weekday = false;
}
